import { flatten } from "lodash";
import normalizeText from "utils/normalizeText";

/**
 * Gets all nodes that contain text in depth-first traversal order
 */
const getTraversalNodes = (root) => {
  const nodes = [];

  function traverse(node) {
    // Handle text nodes
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text.length > 0) {
        nodes.push(node);
      }
      return;
    }

    // Handle element nodes
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.childNodes.length === 0) {
        // Only add if we're not including text nodes, or if it has no children
        nodes.push(node);
      }

      // Traverse children
      node.childNodes.forEach((child) => traverse(child));
    }
  }

  traverse(root);
  return nodes;
};

/**
 * Attempts to match the search string starting from a specific node index
 */
const tryMatchFromNode = (nodes, startIdx, searchText) => {
  let combinedText = "";
  const matchedNodes = [];

  for (let i = startIdx; i < nodes.length; i++) {
    const node = nodes[i];
    const nodeText = normalizeText(node.textContent || "");

    // If the normalized nodeText is "empty", ignore it
    // (Given we're just searching for alphanumeric chars)
    if (!nodeText) {
      continue;
    }

    combinedText += nodeText;
    matchedNodes.push(node);

    // Check if we have a match
    if (combinedText === searchText) {
      return matchedNodes;
    }

    // If we've exceeded the search text length, this sequence doesn't match
    if (combinedText.length > searchText.length) {
      break;
    }

    // Check if we're still on track for a potential match
    if (!searchText.startsWith(combinedText)) {
      break;
    }
  }

  return null;
};

/**
 * Finds sequential DOM nodes whose combined text content matches the search string
 * @param {string} searchText - The text to search for
 * @param {Node} rootNode - The root DOM node to search within
 * @returns {Array<Node>} - Array of matching node sequences
 */
const findSequentialNodes = (searchText, rootNode) => {
  if (!searchText || !rootNode) {
    return [];
  }

  // Normalize the search text
  const normalizedSearch = normalizeText(searchText);

  // Get all text-containing nodes in traversal order
  const nodes = getTraversalNodes(rootNode);

  // Try to find matching sequences
  const matches = [];

  for (let i = 0; i < nodes.length; i++) {
    const match = tryMatchFromNode(nodes, i, normalizedSearch);

    if (match) {
      matches.push(match);
      // Skip past this match to avoid overlapping results
      i += match.length - 1;
    }
  }

  return flatten(matches);
};

export default findSequentialNodes;
